"use client";

import { Spinner } from "@/components/ui/spinner";

import { Dispatch, SetStateAction, useState } from "react";
import { useAppRouter as useRouter } from "@/lib/navigation";


import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { CamundaTask } from "@/lib/camunda";
import {
  CamundaRequestError,
  isCamundaRequestError,
} from "@/lib/camunda/base-service";
import { CamundaProcessService } from "@/lib/camunda/process-service";
import { CamundaUserService } from "@/lib/camunda/user-service";
import { taskToast } from "@/lib/task-toast";
import {
  createBpmEventId,
  trackActionFailed,
  trackActionStarted,
  trackActionSucceeded,
} from "@/lib/telemetry/bpm-telemetry";

interface CancelProcessButtonProps {
  task: CamundaTask;
  isDialogOpen: boolean;
  setIsDialogOpen: Dispatch<SetStateAction<boolean>>;
  canCancelProcess: boolean;
}

function getTelemetryNow(): number {
  return globalThis.performance?.now?.() ?? Date.now();
}

function getDurationMs(startedAt: number): number {
  return Math.max(0, Math.round(getTelemetryNow() - startedAt));
}

function appendErrorCode(description: string, eventId?: string): string {
  return eventId ? `${description}\nCódigo de error: ${eventId}` : description;
}

function getBpmContext(task: CamundaTask) {
  return {
    taskId: task.id,
    processInstanceId: task.processInstanceId,
    processDefinitionId: task.processDefinitionId,
    processKey: task.processDefinitionKey,
    taskDefinitionKey: task.taskDefinitionKey,
    formKey: task.formKey ?? undefined,
    assignee: task.assignee ?? undefined,
  };
}

function getCamundaHttpContext(error: CamundaRequestError) {
  return {
    endpoint: error.endpoint.split("?")[0] || error.endpoint,
    status: error.status,
    statusText: error.statusText,
  };
}

export function CancelProcessButton({
  task,
  isDialogOpen,
  setIsDialogOpen,
  canCancelProcess,
}: CancelProcessButtonProps) {
  const [isCancelling, setIsCancelling] = useState(false);
  const router = useRouter();

  const handleCancelProcess = async () => {
    const eventId = createBpmEventId();
    const startedAt = getTelemetryNow();
    trackActionStarted({
      eventId,
      action: "cancel",
      bpm: getBpmContext(task),
    });

    if (!canCancelProcess) {
      trackActionFailed({
        eventId,
        action: "cancel",
        bpm: getBpmContext(task),
        durationMs: getDurationMs(startedAt),
        error: "No tienes autorizacion para cancelar el proceso",
      });
      taskToast.warning("No tienes autorizacion para cancelar el proceso", {
        description: `Código de error: ${eventId}`,
      });
      return;
    }

    if (!task.processInstanceId) {
      trackActionFailed({
        eventId,
        action: "cancel",
        bpm: getBpmContext(task),
        durationMs: getDurationMs(startedAt),
        error: "No se pudo identificar el proceso",
      });
      taskToast.error("No se pudo identificar el proceso", {
        description: `Código de error: ${eventId}`,
      });
      return;
    }

    const currentUserId = CamundaUserService.getCurrentUserId();
    if (!currentUserId) {
      trackActionFailed({
        eventId,
        action: "cancel",
        bpm: getBpmContext(task),
        durationMs: getDurationMs(startedAt),
        error: "No se pudo obtener el ID del usuario",
      });
      taskToast.error("No se pudo obtener el ID del usuario", {
        description: `Código de error: ${eventId}`,
      });
      return;
    }

    setIsCancelling(true);
    try {
      await CamundaProcessService.cancelProcess(task.processInstanceId, currentUserId);
      trackActionSucceeded({
        eventId,
        action: "cancel",
        bpm: getBpmContext(task),
        durationMs: getDurationMs(startedAt),
      });
      taskToast.success("Proceso cancelado exitosamente");
      router.push("/task-list/tasks");
    } catch (error) {
      const requestError = isCamundaRequestError(error) ? error : null;
      console.error(error);
      trackActionFailed({
        eventId,
        action: "cancel",
        bpm: getBpmContext(task),
        durationMs: getDurationMs(startedAt),
        http: requestError ? getCamundaHttpContext(requestError) : undefined,
        error,
      });
      taskToast.error("No se pudo cancelar el proceso", {
        description: appendErrorCode("El proceso sigue activo. Reintentalo o revisa el log.", eventId),
      });
    }
    setIsCancelling(false);
    setIsDialogOpen(false);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancelar Proceso</DialogTitle>
          <DialogDescription>
            Al <b>cancelar</b> este proceso implica su <b>eliminacion de forma permanente.</b>{" "}
            Esta accion <b>no se puede deshacer.</b> Desea continuar?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex justify-end space-x-2">
          <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancelProcess}
            disabled={isCancelling || !canCancelProcess}
          >
            {isCancelling && <Spinner className="mr-2" />}
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
